import { useCallback, useEffect, useState } from "react";

import Sentry from "utils/sentry";

let existingScript;

const GSI = "https://accounts.google.com/gsi/client";

/**
 * GSI API. Once loaded, returns a `google.accounts.id` instance that has been
 * initialized. The props, such as `callback`, are passed as IdConfiguration
 * parameters.
 *
 * NOTE: if loaded more than once in a page, only the last set of props is used!
 *
 * See:
 * https://developers.google.com/identity/gsi/web/reference/js-reference#google.accounts.id.initialize
 */
const useGoogleIdentity = ({ autoSelect = false, callback, context } = {}) => {
  const [idApi, setIdApi] = useState();
  const init = useCallback(() => {
    try {
      window.google.accounts.id.initialize({
        client_id: env.GOOGLE_OAUTH_CLIENT_ID,
        auto_select: autoSelect,
        callback,
        context,
      });

      setIdApi(window.google.accounts.id);
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [autoSelect, callback, context]);

  useEffect(() => {
    if (window.google?.accounts?.id) {
      init();
      return () => {};
    }

    if (existingScript) {
      // if script tag was added by other element just check when it is loaded
      existingScript.addEventListener("load", init);
      return () => {};
    }

    const script = document.createElement("script");
    script.src = GSI;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", init);
    document.head.appendChild(script);
    existingScript = script;

    return () => {
      script.removeEventListener("load", init);
      script.remove();
      existingScript = null;
    };
  }, [init]);

  return idApi;
};

export default useGoogleIdentity;
